import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Checkbox, Toggle } from '@/components/ui'
import {
  countActiveFilters,
  DEFAULT_QUEUE_FILTERS,
  type DetectedFilter,
  type DirectionFilter,
  type MinPctFilter,
  type QueueFilters,
  type StatusFilter,
} from '../utils/queue-filters'

/**
 * Collapsible left filter rail for the review queue. Same shape and DOM
 * idioms as QuotesFilterRail / CofA Management's rail (Section + ChipGroup),
 * plus a searchable vendor checklist with live per-vendor counts.
 */

const DIRECTION_OPTIONS: { value: DirectionFilter; label: string }[] = [
  { value: 'increase', label: 'Increases' },
  { value: 'decrease', label: 'Decreases' },
  { value: 'all', label: 'All' },
]

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'active', label: 'All active' },
  { value: 'open', label: 'Open' },
  { value: 'reviewing', label: 'Reviewing' },
]

const MIN_PCT_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Any' },
  { value: '5', label: '≥ 5%' },
  { value: '10', label: '≥ 10%' },
  { value: '25', label: '≥ 25%' },
]

const DETECTED_OPTIONS: { value: string; label: string }[] = [
  { value: 'any', label: 'Any time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
]

/** Vendor rows shown before "Show all" — keeps the rail scannable. */
const VENDOR_PREVIEW = 12

export interface PriceChangesFilterRailProps {
  filters: QueueFilters
  onChange: (next: QueueFilters) => void
  vendorOptions: { name: string; count: number }[]
  /** Matches per direction under the other filters and search. */
  directionCounts?: Record<DirectionFilter, number>
  collapsed: boolean
  onToggleCollapsed: () => void
}

export function PriceChangesFilterRail({
  filters, onChange, vendorOptions, directionCounts, collapsed, onToggleCollapsed,
}: PriceChangesFilterRailProps) {
  const [vendorQuery, setVendorQuery] = useState('')
  const [showAllVendors, setShowAllVendors] = useState(false)
  const activeCount = countActiveFilters(filters)

  if (collapsed) {
    return (
      <aside
        className="flex w-8 flex-col items-center border-r border-border-subtle bg-surface-sunk"
        aria-label="Filters (collapsed)"
      >
        <button
          type="button"
          aria-label="Expand filters"
          onClick={onToggleCollapsed}
          className="mt-2 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <ChevronRight size={14} />
        </button>
        {activeCount > 0 ? (
          <span className="text-role-badge-count mt-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-subtle px-1.5 text-accent-text">
            {activeCount}
          </span>
        ) : null}
      </aside>
    )
  }

  const selected = new Set(filters.vendors)
  const q = vendorQuery.trim().toLowerCase()
  const matching = q ? vendorOptions.filter((v) => v.name.toLowerCase().includes(q)) : vendorOptions
  const visibleVendors = showAllVendors || q ? matching : matching.slice(0, VENDOR_PREVIEW)

  function toggleVendor(name: string) {
    const next = new Set(selected)
    if (next.has(name)) next.delete(name)
    else next.add(name)
    onChange({ ...filters, vendors: [...next] })
  }

  return (
    <aside
      aria-label="Filters"
      className="flex w-[var(--layout-filter-rail-width)] shrink-0 flex-col gap-4 overflow-y-auto border-r border-border-subtle bg-surface-sunk p-3"
    >
      <header className="flex items-center justify-between border-b border-border-subtle pb-2">
        <span className="text-role-section-label normal-case">
          Filters
          {activeCount > 0 ? (
            <span className="ml-1.5 text-accent-text">· {activeCount}</span>
          ) : null}
        </span>
        <span className="flex items-center gap-1">
          {activeCount > 0 ? (
            <button
              type="button"
              onClick={() => onChange(DEFAULT_QUEUE_FILTERS)}
              className="cursor-pointer border-none bg-transparent px-1 text-xs text-accent-text hover:underline"
            >
              Clear
            </button>
          ) : null}
          <button
            type="button"
            aria-label="Collapse filters"
            onClick={onToggleCollapsed}
            className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
          >
            <ChevronLeft size={14} />
          </button>
        </span>
      </header>

      <Section label="Direction">
        <ChipGroup
          options={DIRECTION_OPTIONS.map((o) =>
            directionCounts ? { ...o, label: `${o.label} ${directionCounts[o.value].toLocaleString()}` } : o,
          )}
          value={filters.direction}
          onChange={(v) => onChange({ ...filters, direction: v })}
        />
      </Section>

      <Section label="Status">
        <ChipGroup
          options={STATUS_OPTIONS}
          value={filters.status}
          onChange={(v) => onChange({ ...filters, status: v })}
        />
      </Section>

      <Section label="Size of change">
        <ChipGroup
          options={MIN_PCT_OPTIONS}
          value={String(filters.minPct)}
          onChange={(v) => onChange({ ...filters, minPct: Number(v) as MinPctFilter })}
        />
      </Section>

      <Section label="Detected">
        <ChipGroup
          options={DETECTED_OPTIONS}
          value={String(filters.detectedWithinDays)}
          onChange={(v) =>
            onChange({
              ...filters,
              detectedWithinDays: (v === 'any' ? 'any' : Number(v)) as DetectedFilter,
            })
          }
        />
      </Section>

      <Section label="Options">
        <Toggle
          checked={filters.onlyPoCollisions}
          onChange={(e) => onChange({ ...filters, onlyPoCollisions: e.target.checked })}
          label="Only with open PO collisions"
        />
      </Section>

      <Section
        label={`Vendor${filters.vendors.length ? ` · ${filters.vendors.length} selected` : ''}`}
      >
        <input
          type="text"
          value={vendorQuery}
          onChange={(e) => setVendorQuery(e.target.value)}
          placeholder="Find a vendor"
          aria-label="Find a vendor"
          className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
        />
        <ul className="flex flex-col gap-1" aria-label="Vendors">
          {visibleVendors.map((v) => (
            <li key={v.name} className="flex items-center justify-between gap-2">
              <Checkbox
                checked={selected.has(v.name)}
                onChange={() => toggleVendor(v.name)}
                label={<span className="truncate text-sm" title={v.name}>{v.name}</span>}
                className="min-w-0"
              />
              <span className="text-role-badge-count shrink-0 text-tertiary">{v.count}</span>
            </li>
          ))}
          {visibleVendors.length === 0 ? (
            <li className="text-role-metadata">No vendors match.</li>
          ) : null}
        </ul>
        <div className="flex gap-3">
          {!q && matching.length > VENDOR_PREVIEW ? (
            <button
              type="button"
              onClick={() => setShowAllVendors((s) => !s)}
              className="cursor-pointer border-none bg-transparent p-0 text-xs text-accent-text hover:underline"
            >
              {showAllVendors ? 'Show fewer' : `Show all ${matching.length}`}
            </button>
          ) : null}
          {filters.vendors.length ? (
            <button
              type="button"
              onClick={() => onChange({ ...filters, vendors: [] })}
              className="cursor-pointer border-none bg-transparent p-0 text-xs text-accent-text hover:underline"
            >
              Clear vendors
            </button>
          ) : null}
        </div>
      </Section>
    </aside>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <header className="text-role-section-label normal-case">{label}</header>
      {children}
    </section>
  )
}

function ChipGroup<T extends string>({
  options, value, onChange,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className="inline-flex items-center gap-1 rounded-xs border border-border-default bg-surface px-2.5 py-1 text-xs text-primary hover:bg-surface-hover aria-pressed:border-accent aria-pressed:bg-accent-subtle aria-pressed:text-accent-text"
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
